'use client';

/**
 * SPEC-095 (Profileia Hub): CopyLinkButton — client component that copies
 * the profile URL to the clipboard and shows a "Copiado" confirmation.
 *
 * Why a Client Component: clipboard write (`navigator.clipboard.writeText`)
 * requires a user gesture in most browsers — it must run in a click
 * handler, not during SSR. The server-rendered fallback (the original
 * "Compartir" link button on /p/[username]) is a plain anchor — the
 * CopyLinkButton augments the action row with a button that needs
 * browser-side API access.
 *
 * §6 Anti-Invención: pure UI utility — no business logic.
 */

import { useCallback, useRef, useState } from 'react';
import { Check, Copy } from 'lucide-react';

interface CopyLinkButtonProps {
  /** The URL to copy. */
  url: string;
  /** className for the button — caller controls styling. */
  className?: string;
}

export function CopyLinkButton({ url, className }: CopyLinkButtonProps) {
  const [copied, setCopied] = useState(false);
  // Track the setTimeout ID so it can be cleared on unmount (prevents
  // "setState on unmounted component" warning — same pattern as the
  // VirtualCard copy button in components/profile/virtual-card.tsx:83).
  const copiedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      if (copiedTimeoutRef.current) clearTimeout(copiedTimeoutRef.current);
      copiedTimeoutRef.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback: clipboard API unavailable (older browsers, insecure
      // context without HTTPS). Silently fail — the user can still copy
      // the URL from the address bar or the ProfileUrlDisplay below.
      // (We intentionally don't use the legacy `document.execCommand('copy')`
      // pattern — it's deprecated and produces a console warning in
      // modern browsers.)
    }
  }, [url]);

  return (
    <button type="button" onClick={handleCopy} className={className} aria-label="Copiar enlace del perfil">
      <span className="inline-flex items-center gap-1.5">
        {copied ? (
          <>
            <Check className="size-3.5" aria-hidden="true" />
            <span>Copiado</span>
          </>
        ) : (
          <>
            <Copy className="size-3.5" aria-hidden="true" />
            <span>Copiar enlace</span>
          </>
        )}
      </span>
    </button>
  );
}
